import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Sparkles, Send, Loader2, Plus, Trash2, MessageSquare, Bot, User as UserIcon, ChevronRight,
} from 'lucide-react';

const AiAdvisorPage = () => {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try {
      const r = await api.get('/ai-advisor/sessions');
      setSessions(r.data?.items || []);
    } catch {}
  }, []);

  useEffect(() => {
    Promise.all([
      api.get('/ai-advisor/sessions').then((r) => setSessions(r.data?.items || [])),
      api.get('/ai-advisor/suggestions').then((r) => setSuggestions(r.data?.items || [])),
    ]).finally(() => setLoading(false));
  }, []);

  const openSession = async (id) => {
    setActiveId(id);
    setMessages([]);
    try {
      const r = await api.get(`/ai-advisor/sessions/${id}/messages`);
      setMessages(r.data?.messages || []);
    } catch {
      toast.error('No se pudo cargar la conversación');
    }
  };

  const newSession = () => {
    setActiveId(null);
    setMessages([]);
    setInput('');
  };

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    setInput('');
    setSending(true);
    const userMsg = { id: 'tmp-' + Date.now(), role: 'user', content: message, created_at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    try {
      const r = await api.post('/ai-advisor/chat', { message, session_id: activeId });
      const assistantMsg = { id: 'tmp-a-' + Date.now(), role: 'assistant', content: r.data.reply, created_at: new Date().toISOString() };
      setMessages((m) => [...m, assistantMsg]);
      if (!activeId) {
        setActiveId(r.data.session_id);
        loadSessions();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error generando respuesta');
      setMessages((m) => m.slice(0, -1));  // remove optimistic user msg on error
    } finally {
      setSending(false);
    }
  };

  const onDeleteSession = async (id) => {
    if (!window.confirm('¿Borrar esta conversación?')) return;
    try {
      await api.delete(`/ai-advisor/sessions/${id}`);
      if (activeId === id) newSession();
      loadSessions();
    } catch {
      toast.error('Error borrando');
    }
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FF6B4A]" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 h-[calc(100vh-160px)]" data-testid="ai-advisor-page">
      {/* Sidebar */}
      <aside className="bg-white rounded-2xl border border-stone-100 p-3 overflow-y-auto">
        <Button onClick={newSession} className="w-full bg-[#FF6B4A] text-white mb-3" data-testid="new-chat-btn">
          <Plus className="w-4 h-4 mr-1" /> Nueva consulta
        </Button>
        <p className="text-[10px] uppercase font-bold text-stone-400 px-1 mb-1">Historial</p>
        {sessions.length === 0 ? (
          <p className="text-xs text-stone-400 p-2">Sin conversaciones previas</p>
        ) : sessions.map((s) => (
          <div key={s.id}
               className={`group flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs cursor-pointer mb-0.5 ${activeId === s.id ? 'bg-stone-900 text-white' : 'hover:bg-stone-100 text-stone-700'}`}>
            <button onClick={() => openSession(s.id)} className="flex-1 text-left min-w-0 truncate" data-testid={`session-item-${s.id}`}>
              <MessageSquare className="w-3 h-3 inline mr-1.5 opacity-60" />
              {s.title}
            </button>
            <button onClick={() => onDeleteSession(s.id)} className="opacity-0 group-hover:opacity-100 transition" title="Borrar" data-testid={`delete-session-${s.id}`}>
              <Trash2 className="w-3 h-3 text-rose-500" />
            </button>
          </div>
        ))}
      </aside>

      {/* Chat area */}
      <main className="bg-white rounded-2xl border border-stone-100 flex flex-col overflow-hidden">
        <header className="border-b border-stone-100 px-5 py-3 flex items-center gap-2">
          <Bot className="w-5 h-5 text-[#FF6B4A]" />
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-stone-900">Asesor IA fiscal</h1>
            <p className="text-[11px] text-stone-500">Potenciado por Claude. Conoce tus números en tiempo real.</p>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="chat-area">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-14 h-14 bg-gradient-to-br from-[#FF6B4A] to-[#FF9966] rounded-2xl flex items-center justify-center mb-3">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-xl font-bold text-stone-900 mb-1">¿En qué te ayudo hoy?</h2>
              <p className="text-sm text-stone-500 mb-5">Pregúntame sobre impuestos, rentabilidad, caja o decisiones de negocio.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl w-full">
                {suggestions.map((s, i) => (
                  <button key={i}
                          onClick={() => send(s.prompt)}
                          className="text-left bg-stone-50 hover:bg-stone-100 border border-stone-100 hover:border-stone-200 rounded-xl p-3 transition group"
                          data-testid={`suggestion-${i}`}>
                    <p className="text-sm font-medium text-stone-900 flex items-center gap-2">
                      <span>{s.icon}</span> {s.title}
                      <ChevronRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 text-[#FF6B4A] transition" />
                    </p>
                    <p className="text-[11px] text-stone-500 mt-0.5 line-clamp-2">{s.prompt}</p>
                  </button>
                ))}
              </div>
            </div>
          ) : messages.map((m) => (
            <MessageBubble key={m.id} m={m} />
          ))}
          {sending && (
            <div className="flex gap-2 items-center text-sm text-stone-500" data-testid="thinking">
              <Bot className="w-4 h-4" /> <Loader2 className="w-3 h-3 animate-spin" /> Pensando...
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Input */}
        <div className="border-t border-stone-100 p-3">
          <div className="flex gap-2 items-end">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Pregunta lo que quieras sobre tu negocio..."
              rows={1}
              className="resize-none rounded-xl flex-1"
              data-testid="chat-input"
              disabled={sending}
            />
            <Button
              onClick={() => send()}
              disabled={sending || !input.trim()}
              className="bg-[#FF6B4A] text-white"
              data-testid="send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-[10px] text-stone-400 mt-1.5">Las respuestas usan tus datos reales. <em>Estimaciones orientativas, contrasta con tu asesor.</em></p>
        </div>
      </main>
    </div>
  );
};

const MessageBubble = ({ m }) => {
  const isUser = m.role === 'user';
  return (
    <div className={`flex gap-2 ${isUser ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.role}`}>
      {!isUser && <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF6B4A] to-[#FF9966] flex items-center justify-center flex-shrink-0"><Bot className="w-4 h-4 text-white" /></div>}
      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${isUser ? 'bg-stone-900 text-white' : 'bg-stone-50 text-stone-900 border border-stone-100'}`}>
        <MarkdownLite text={m.content} />
      </div>
      {isUser && <div className="w-7 h-7 rounded-full bg-stone-200 flex items-center justify-center flex-shrink-0"><UserIcon className="w-4 h-4 text-stone-600" /></div>}
    </div>
  );
};

// Ultra-light Markdown rendering for bold + bullets + paragraphs.
const MarkdownLite = ({ text }) => {
  if (!text) return null;
  // Bold: **xxx**
  const renderInline = (s) => {
    const parts = s.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    return parts.map((p, i) => {
      if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
      if (/^\*[^*]+\*$/.test(p)) return <em key={i}>{p.slice(1, -1)}</em>;
      return <span key={i}>{p}</span>;
    });
  };
  const blocks = text.split(/\n\n+/);
  return (
    <div className="space-y-2">
      {blocks.map((block, bi) => {
        const lines = block.split('\n').filter(Boolean);
        const isList = lines.every((ln) => /^\s*[-*]\s+/.test(ln) || /^\s*\d+\.\s+/.test(ln));
        if (isList && lines.length > 1) {
          return (
            <ul key={bi} className="list-disc pl-5 space-y-0.5">
              {lines.map((ln, li) => (
                <li key={li}>{renderInline(ln.replace(/^\s*[-*]\s+|^\s*\d+\.\s+/, ''))}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={bi} className="whitespace-pre-wrap leading-relaxed">{renderInline(block)}</p>
        );
      })}
    </div>
  );
};

export default AiAdvisorPage;
